import { IconButton } from '@carbon/react';
import { Close, ChevronUp, ChevronDown, Launch } from '@carbon/react/icons';
import styles from './document-viewer.module.scss';

interface Props {
  documentId?: number;
  onClose?: () => void;
  documentName?: string;
  // Optional preview extras — the /documents page passes none of these, so it gets
  // no subtitle, nav, pop-out or PDF-toolbar hiding; Map Query opts into the mock
  // design. The close control is shared: both pages now render it as the Carbon
  // ghost IconButton below (the /documents close icon changed with it).
  subtitle?: string;
  hideToolbar?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

export function DocumentViewer({
  documentId,
  onClose,
  documentName,
  subtitle,
  hideToolbar,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: Props) {
  if (!documentId) {
    return null;
  }

  // The iframe embeds the document PDF; the fragment hides the browser's native
  // PDF toolbar for a clean, mock-style preview (full doc still opens via pop-out).
  const src = `/documents/${documentId}/image${
    hideToolbar ? '#toolbar=0&navpanes=0&view=FitH' : ''
  }`;

  return (
    <div className={styles.viewer}>
      <div
        className={[styles.header, subtitle ? styles.headerWithSubtitle : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.headerText}>
          <h4>{documentName || `Document #${documentId}`}</h4>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div className={styles.headerActions}>
          {onPrev && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              label="Previous document"
              disabled={!hasPrev}
              onClick={onPrev}
            >
              <ChevronUp />
            </IconButton>
          )}
          {onNext && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              label="Next document"
              disabled={!hasNext}
              onClick={onNext}
            >
              <ChevronDown />
            </IconButton>
          )}
          {hideToolbar && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              label="Open in new tab"
              onClick={() =>
                window.open(
                  `/documents/${documentId}/image`,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              <Launch />
            </IconButton>
          )}
          {onClose && (
            <IconButton
              kind="ghost"
              size="sm"
              align="bottom"
              label="Close preview"
              onClick={onClose}
            >
              <Close />
            </IconButton>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <iframe src={src} className={styles.iframe} title="Document Viewer" />
      </div>
    </div>
  );
}
